import type { PropsWithChildren } from 'react';

import { useAtomValue } from 'jotai';

import type { Unit, UserPreferredUnit } from '@core';
import { convertUnit, DisplaySymbolMap, isFiniteNumber, isUnit } from '@core';
import { useTranslate } from '@i18n';
import { activityUnitAtom, uiNodeBySmpIdAtom } from '@store';
import { Box, Stack, SwingFoundationParameterCard, Text, u } from '@ui';

import { useSwingGuideMetric } from './useSwingGuideMetric';

type SwingGuideMetricsProps = {
    smpId: number;
};

const formatFiniteValue = (value: number | null | undefined, suffix = '') =>
    isFiniteNumber(value) ? `${value}${suffix}` : '-';

const formatDisplayValue = (value: number, symbol: string) => formatFiniteValue(value, symbol);

const formatRange = (min: number, max: number, symbol: string) =>
    `${formatDisplayValue(min, symbol)} to ${formatDisplayValue(max, symbol)}`;

const formatConvertedValue = (
    value: number | null | undefined,
    unit: Unit,
    userPreferredUnit: UserPreferredUnit,
    includeSymbol = true,
) => {
    const { symbol, value: convertedValue } = convertUnit({ unit, userPreferredUnit, value });
    return formatFiniteValue(convertedValue, includeSymbol ? symbol : '');
};

export const SwingGuideMetrics = ({ smpId }: SwingGuideMetricsProps) => {
    const t = useTranslate();
    const selectedParameterNode = useAtomValue(uiNodeBySmpIdAtom)[smpId];
    const userPreferredUnit = useAtomValue(activityUnitAtom);
    const parameter = selectedParameterNode?.parameter;

    const {
        currentValue: value,
        reportValue,
        corridor: corridorParameterMargins,
        goalCorridor,
    } = useSwingGuideMetric(smpId);

    if (!parameter || !isUnit(parameter.unit)) return null;
    const unit = parameter.unit;
    const title = t(selectedParameterNode.i18n ?? '');
    const currentDisplayValue = formatConvertedValue(value, unit, userPreferredUnit);

    return (
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="flex-start" gap={u(2)}>
            <Stack flex={1} width="100%" minWidth="fit-content">
                <SwingFoundationParameterCard
                    shadow={1}
                    title={title}
                    unit={unit}
                    userPreferredUnit={userPreferredUnit}
                    value={value}
                    mean={reportValue?.mean}
                    stdDev={reportValue?.stdDev}
                    corridorParameterMargins={corridorParameterMargins}
                    goalCorridor={goalCorridor}
                />
            </Stack>

            <Stack flex={1} minWidth={0} gap={u(1)}>
                <MetricDetail label={t('swing_guide.summary_title')}>{title}</MetricDetail>
                <MetricDetail label={t('swing_guide.swing_value')}>{currentDisplayValue}</MetricDetail>
                {goalCorridor && (
                    <MetricDetail label={t('swing_guide.user_corridor')}>
                        {formatRange(goalCorridor.innerMin, goalCorridor.innerMax, goalCorridor.unit)}
                    </MetricDetail>
                )}
                {corridorParameterMargins && (
                    <MetricDetail label={t('swing_guide.elva_corridor')}>
                        {formatRange(
                            corridorParameterMargins.innerMin,
                            corridorParameterMargins.innerMax,
                            DisplaySymbolMap[corridorParameterMargins.unit],
                        )}
                    </MetricDetail>
                )}
                <MetricDetail label={t('swing_guide.session_average')}>
                    {formatConvertedValue(reportValue?.mean, unit, userPreferredUnit, false)}
                </MetricDetail>
                <MetricDetail label={t('swing_guide.consistency')}>
                    {formatConvertedValue(reportValue?.stdDev, unit, userPreferredUnit, false)}
                </MetricDetail>
            </Stack>
        </Box>
    );
};

type MetricDetailProps = {
    label: string;
};

const MetricDetail = ({ label, children }: PropsWithChildren<MetricDetailProps>) => (
    <Text>
        <strong>{label}</strong> {children}
    </Text>
);
